import type {
  Answer,
  FieldDefinition,
  FieldId,
  ValidationIssue,
} from '@inside-us-aos/domain';
import { validateOneOfGroups } from '@inside-us-aos/domain';

import { resolveVisibleFieldIds } from '../engine/visibility';
import { enforceRepeatableBounds } from '../repeatables/state';
import { validateField } from './perField';

/**
 * One-of-groups integration; Phase 1.1 — repeatable
 * bound integration.
 *
 * Composes the engine's three issue sources into a single result:
 *   1. Per-field issues (Slice 3 — `validateField`).
 *   2. Cross-field one-of-group issues (domain `validateOneOfGroups`).
 *   3. Repeatable-group bound issues (Slice 5 — `enforceRepeatableBounds`).
 *
 * The domain `validateOneOfGroups` helper implements the visibility-
 * aware semantics (only enforced when ≥1 member is visible; satisfied
 * when ≥1 visible member has a present answer). The Slice 5
 * `enforceRepeatableBounds` helper emits `value_out_of_range` issues
 * for groups below `min` or — when `overflowStrategy === 'reject'` —
 * over `max`. Other overflow strategies (`additional_info`,
 * `truncate`) are NOT issues; they surface only as `state.overflow`
 * descriptors via `getRepeatableState()`.
 *
 * The Phase 1 review (P1) found that `getValidationState()` excluded
 * bound issues — so a consumer asking "is the answer set valid?" got
 * a false-positive clean state when, e.g., `sponsor.tax_history` had
 * fewer than its required 3 instances. This module now folds them in
 * by default. `getRepeatableBoundIssues()` is preserved on the engine
 * façade as an escape hatch for consumers that want only that subset.
 *
 * Issue ordering is stable: per-field issues first (catalog order),
 * then one-of-group issues (catalog order of the anchor field), then
 * repeatable bound issues (catalog order of the anchor field).
 */

export interface ValidateAnswersResult {
  issues: readonly ValidationIssue[];
  /**
   * Per-field index. One-of-group issues are indexed under their
   * anchor field id (the alphabetically-first visible member); bound
   * issues are indexed under the group's anchor field id (also the
   * alphabetically-first visible member, post-fix-5).
   */
  issuesByFieldId: ReadonlyMap<FieldId, readonly ValidationIssue[]>;
}

/**
 * Full validation pass over a catalog: per-field issues, plus one-of-
 * group issues, plus repeatable-bound issues. Hidden fields contribute
 * no issues; one-of groups whose members are all hidden are skipped
 * entirely (per the domain helper's contract).
 */
export const validateAnswers = (
  catalog: readonly FieldDefinition[],
  answers: readonly Answer[] = [],
): ValidateAnswersResult => {
  const visibleFieldIds = resolveVisibleFieldIds(catalog, answers);
  const answerByFieldId = new Map(
    answers.map(answer => [answer.fieldId, answer]),
  );

  const issues: ValidationIssue[] = [];
  const issuesByFieldId = new Map<FieldId, ValidationIssue[]>();

  const recordIssue = (issue: ValidationIssue): void => {
    issues.push(issue);
    const existing = issuesByFieldId.get(issue.fieldId);
    if (existing) {
      existing.push(issue);
    } else {
      issuesByFieldId.set(issue.fieldId, [issue]);
    }
  };

  for (const field of catalog) {
    const fieldIssues = validateField(
      field,
      answerByFieldId.get(field.id),
      answers,
      visibleFieldIds,
    );
    for (const issue of fieldIssues) recordIssue(issue);
  }

  for (const issue of validateOneOfGroups(catalog, answers)) {
    recordIssue(issue);
  }

  for (const issue of enforceRepeatableBounds(catalog, answers)) {
    recordIssue(issue);
  }

  return { issues, issuesByFieldId };
};
